import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import {
    Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Dialog, DialogActions, DialogContent, DialogTitle, TextField, IconButton, Typography,
    Stack, Card, CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Box,
    Avatar,
    Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { formatDate } from '../library';
import { CustomSelectField, CustomTextField } from '../components/customStyle';
import DeleteDialog from '../components/dialogDelete';
import LanguageSelect from '../components/language/select';
import { useLanguage } from '../contexts/LanguageContext';

const Users = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<any[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editUser, setEditUser] = useState<any>(null);
    const [newUser, setNewUser] = useState<{ name: string, email: string, role: string }>({ name: '', email: '', role: '' });
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [selectedUserName, setSelectedUserName] = useState<string>('')
    const { language, setLanguage } = useLanguage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const db = getFirestore();
    const usersCollection = collection(db, 'users');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        const userSnapshot = await getDocs(usersCollection);
        const userList = userSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(userList);
    };

    const handleCreateUser = () => {
        setEditUser(null);
        setNewUser({ name: '', email: '', role: '' });
        setOpenDialog(true);
    };

    const handleEditUser = (user: any) => {
        if (user) {
            setEditUser(user);
            setNewUser({ name: user.name, email: user.email, role: user.role });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditUser(null);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewUser({ ...newUser, [e.target.name]: e.target.value });
    };

    const handleSaveUser = async () => {
        if (editUser) {
            const userRef = doc(db, 'users', editUser.id);
            await updateDoc(userRef, { ...newUser, updated: new Date() });
        } else {
            await addDoc(usersCollection, { ...newUser, created: new Date(), updated: new Date() });
        }
        fetchUsers();
        handleCloseDialog();
    };

    const handleDeleteUser = async () => {
        if (!selectedUserId) return;
        try {
            const userRef = doc(db, 'users', selectedUserId);
            await deleteDoc(userRef);
            setOpenDeleteDialog(false)
            fetchUsers();
        } catch (error) {
            console.log('error', error)
        }
    };

    const handleOpenDeleteDialog =(id: string, name:string)=>{
        setOpenDeleteDialog(true)
        setSelectedUserId(id)
        setSelectedUserName(name)
    }

    return (
        <div style={{ padding: isMobile ? '16px' : '32px', background: '#fdfbf7', minHeight: '100vh' }}>
            <Stack direction={"row"} spacing={2} alignItems="center" justifyContent={'space-between'}>
                <Button
                    variant="outlined"
                    size='small'
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        color: '#8b7355',
                        borderColor: 'rgba(139, 115, 85, 0.3)',
                        "&:hover": {
                            background: 'rgba(139, 115, 85, 0.08)',
                            borderColor: 'rgba(139, 115, 85, 0.5)',
                        },
                        width: 'auto',
                        mb: isMobile ? 2 : 0
                    }}
                >
                    {language === 'en' ? 'Back' : 'Trở lại'}
                </Button>
                <LanguageSelect value={language} onChange={(value)=>setLanguage(value)} />
            </Stack>

            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "stretch" : "center"} mt={4} spacing={isMobile ? 2 : 0}>
                <Typography
                    variant={isMobile ? "h6" : "h5"}
                    sx={{ 
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        color: '#4a3f35',
                        letterSpacing: '-0.5px'
                    }}
                >
                    {language === 'en' ? 'Users Management' : 'Quản lý người dùng'}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateUser}
                    sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        backgroundColor: '#6b8e51',
                        color: '#fff',
                        boxShadow: '0 4px 12px rgba(107, 142, 81, 0.25)',
                        px: 3,
                        py: 1,
                        width: isMobile ? '100%' : 'auto',
                        '&:hover': { 
                            backgroundColor: '#5a7d46',
                            boxShadow: '0 6px 16px rgba(107, 142, 81, 0.35)',
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                    }}
                >
                    {language === 'en' ? 'Add User' : 'Thêm người dùng'}
                </Button>
            </Stack>

            {isMobile ? (
                <Stack spacing={2} mt={4}>
                    {users.length > 0 ? (
                        users.map(user => (
                            <Card key={user.id} sx={{ borderRadius: '16px', boxShadow: '0 8px 32px rgba(139, 115, 85, 0.08)', border: '1px solid rgba(139, 115, 85, 0.1)', background: '#fff' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" alignItems="center" spacing={2} mb={2} pb={2} borderBottom="1px dashed rgba(139, 115, 85, 0.2)">
                                        <Avatar sx={{ width: 48, height: 48, bgcolor: 'rgba(139, 115, 85, 0.1)', color: '#8b7355', fontWeight: 600, fontSize: '1.2rem' }}>
                                            {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                                        </Avatar>
                                        <Box flex={1} sx={{ minWidth: 0 }}>
                                            <Typography noWrap variant="subtitle1" sx={{ fontFamily: 'Inter, sans-serif', color: '#4a3f35', fontWeight: 600 }}>
                                                {user.name}
                                            </Typography>
                                            <Typography noWrap variant="body2" sx={{ fontFamily: 'Inter, sans-serif', color: '#8b7355', fontWeight: 500, fontSize: '0.8rem' }}>
                                                {user.email}
                                            </Typography>
                                        </Box>
                                        <span style={{ 
                                            padding: '4px 10px', 
                                            borderRadius: '12px', 
                                            background: user.role === 'admin' ? 'rgba(201, 106, 82, 0.1)' : 'rgba(107, 142, 81, 0.1)', 
                                            color: user.role === 'admin' ? '#c96a52' : '#6b8e51', 
                                            fontWeight: 600, 
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {user.role}
                                        </span>
                                    </Stack>
                                    <Stack spacing={1.5}>
                                        <Typography variant="body2" sx={{ fontFamily: 'Inter, sans-serif', color: '#8b7355', fontWeight: 500, display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{language === 'en' ? 'ID' : 'Mã'}:</span> <span style={{ color: '#b0a395', fontFamily: 'monospace' }}>{user.id.substring(0, 8)}...</span>
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: 'Inter, sans-serif', color: '#8b7355', fontWeight: 500, display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{language === 'en' ? 'Created' : 'Ngày tạo'}:</span> <span>{formatDate(user.created)}</span>
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: 'Inter, sans-serif', color: '#8b7355', fontWeight: 500, display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{language === 'en' ? 'Updated' : 'Ngày cập nhật'}:</span> <span>{formatDate(user.updated)}</span>
                                        </Typography>
                                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 2, mt: 1 }}>
                                            <IconButton sx={{ color: '#6b8e51', background: 'rgba(107, 142, 81, 0.1)' }} onClick={() => handleEditUser(user)} size="small">
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton sx={{ color: '#c96a52', background: 'rgba(201, 106, 82, 0.1)' }} onClick={() => handleOpenDeleteDialog(user.id, user.name)} size="small">
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))
                    ) : (
                        <Typography align="center" sx={{ color: '#8b7355', mt: 4 }}>No data...</Typography>
                    )}
                </Stack>
            ) : (
                <TableContainer component={Paper} sx={{ 
                    marginTop: 4, 
                    borderRadius: '16px', 
                    boxShadow: '0 8px 32px rgba(139, 115, 85, 0.08)',
                    border: '1px solid rgba(139, 115, 85, 0.1)',
                    overflow: 'hidden'
                }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ background: '#f5f2eb' }}>
                                {['ID', 'Name', 'Email', 'Role', 'Created', 'Updated', 'Actions'].map((header, idx) => (
                                    <TableCell key={header} sx={{ fontFamily: 'Inter, sans-serif', color: '#8b7355', fontWeight: 600, borderBottom: '1px solid rgba(139, 115, 85, 0.1)', textAlign: header === 'Actions' ? 'center' : 'left', textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '0.5px' }}>
                                        {language === 'en' ? header : ['Mã', 'Tên', 'Email', 'Vai trò', 'Ngày tạo', 'Ngày cập nhật', 'Hành động'][idx]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.length > 0 ? (
                                users.map(user => (
                                    <TableRow key={user.id} sx={{ '&:hover': { background: 'rgba(139, 115, 85, 0.04)' }, '& td': { borderColor: 'rgba(139, 115, 85, 0.1)', color: '#4a3f35', fontFamily: 'Inter, sans-serif', py: 2 } }}>
                                        <TableCell sx={{ fontWeight: 500, color: '#b0a395', fontFamily: 'monospace' }}>{user.id.substring(0, 8)}...</TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(139, 115, 85, 0.1)', color: '#8b7355', fontWeight: 600, fontSize: '1rem' }}>
                                                    {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                                                </Avatar>
                                                <Typography sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', color: '#4a3f35' }}>{user.name}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{user.email}</TableCell>
                                        <TableCell>
                                            <span style={{ 
                                                padding: '6px 12px', 
                                                borderRadius: '12px', 
                                                background: user.role === 'admin' ? 'rgba(201, 106, 82, 0.1)' : 'rgba(107, 142, 81, 0.1)', 
                                                color: user.role === 'admin' ? '#c96a52' : '#6b8e51', 
                                                fontSize: '0.8rem', 
                                                fontWeight: 600, 
                                                textTransform: 'uppercase', 
                                                letterSpacing: '0.5px' 
                                            }}>
                                                {user.role}
                                            </span>
                                        </TableCell>
                                        <TableCell sx={{ color: '#8b7355', fontSize: '0.9rem', fontWeight: 500 }}>{formatDate(user.created)}</TableCell>
                                        <TableCell sx={{ color: '#8b7355', fontSize: '0.9rem', fontWeight: 500 }}>{formatDate(user.updated)}</TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <Tooltip title={language === 'en' ? 'Edit' : 'Chỉnh sửa'}>
                                                    <IconButton sx={{ color: '#6b8e51', background: 'rgba(107, 142, 81, 0.1)', '&:hover': { background: '#6b8e51', color: '#fff' }, transition: 'all 0.2s' }} onClick={() => handleEditUser(user)} size="small">
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={language === 'en' ? 'Delete' : 'Xóa'}>
                                                    <IconButton sx={{ color: '#c96a52', background: 'rgba(201, 106, 82, 0.1)', '&:hover': { background: '#c96a52', color: '#fff' }, transition: 'all 0.2s' }} onClick={() => handleOpenDeleteDialog(user.id, user.name)} size="small">
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ color: '#8b7355', py: 6 }}>No data...</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog 
                open={openDialog} 
                fullScreen={isMobile} 
                maxWidth="sm" 
                PaperProps={{
                    sx: {
                        width: isMobile ? '100%' : '480px',
                        maxWidth: isMobile ? '100%' : '480px',
                        borderRadius: isMobile ? 0 : '24px',
                        background: '#fdfbf7',
                        boxShadow: '0 24px 60px rgba(74, 59, 50, 0.15)'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    fontFamily: 'Inter, sans-serif', 
                    fontWeight: 700, 
                    color: '#4a3f35', 
                    borderBottom: '1px solid rgba(139, 115, 85, 0.1)', 
                    pb: 2.5,
                    pt: 3,
                    px: 3,
                    background: 'linear-gradient(to right, rgba(139, 115, 85, 0.08), rgba(253, 251, 247, 0))'
                }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: 'rgba(107, 142, 81, 0.15)', color: '#6b8e51', width: 44, height: 44 }}>
                            {editUser ? <EditIcon /> : <PersonRoundedIcon />}
                        </Avatar>
                        <span>{language === 'en' ? editUser ? 'Edit User' : 'Add New User' : editUser ? 'Chỉnh sửa người dùng' : 'Thêm người dùng mới'}</span>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ p: '24px 24px 32px 24px !important' }}>
                    <Stack spacing={3}>
                        <CustomTextField label={language === 'en' ? 'Name' : 'Tên'} name="name" value={newUser.name} onChange={handleChange}/>
                        <CustomTextField label={language === 'en' ? 'Email' : 'Email'} name="email" value={newUser.email} onChange={handleChange} />
                        <CustomSelectField
                            label={language === 'en' ? 'Role' : 'Vai trò'}
                            name="role"
                            value={newUser.role}
                            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                            options={[
                                { label: language === 'en' ? 'Admin' : 'Quản trị viên', value: 'admin' },
                                { label: language === 'en' ? 'Reader' : 'Người đọc', value: 'reader' }
                            ]}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(139, 115, 85, 0.1)', display: 'flex', gap: 1 }}>
                    <Button 
                        variant="outlined" 
                        fullWidth
                        startIcon={<CloseRoundedIcon />}
                        onClick={handleCloseDialog}
                        sx={{ 
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: '#8b7355',
                            borderColor: 'rgba(139, 115, 85, 0.3)',
                            py: 1,
                            '&:hover': { background: 'rgba(139, 115, 85, 0.08)', borderColor: 'rgba(139, 115, 85, 0.5)' }
                        }}
                    >
                        {language === 'en' ? 'Cancel' : 'Hủy bỏ'}
                    </Button>
                    <Button 
                        variant="contained" 
                        fullWidth
                        startIcon={<SaveRoundedIcon />}
                        onClick={handleSaveUser}
                        sx={{ 
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            backgroundColor: '#6b8e51',
                            color: '#fff',
                            boxShadow: '0 4px 12px rgba(107, 142, 81, 0.25)',
                            py: 1,
                            '&:hover': { 
                                backgroundColor: '#5a7d46',
                                boxShadow: '0 6px 16px rgba(107, 142, 81, 0.35)',
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {language === 'en' ? editUser ? 'Update' : 'Save' : editUser ? 'Cập nhật' : 'Lưu'}
                    </Button>
                </DialogActions>
            </Dialog>

            <DeleteDialog open={openDeleteDialog} handleClose={() => setOpenDeleteDialog(false)} handleDelete={handleDeleteUser} message={`${language === 'en' ? 'Are you sure want to delete' : 'Bạn có chắc muốn xóa'} ${selectedUserName} ?`}/>
        </div>
    );
};

export default Users;
